import { GET_TOC_ANNOUNCEMENTS } from '../../../src/client/models/table-of-contents/queries.gql';
import { nextReduxWrapper } from '../../../src/client/store/create-store';
import AcquisitionLandingPage from '../../../src/client/pages/acquisition-landing';
import fetchAllProductData from '../../../src/client/models/product/fetch-all-product-data';
import fetchProductsFromParams from '../../../src/client/models/product/fetch-products-from-params';
import getAnnouncementsQuery from '../../../pages/utils/get-announcements-query';
import getOffersContent from '../../../pages/utils/get-offers-content';
import getSaleableCombinedProductIds from '../../../src/client/utils/get-saleable-combined-product-ids';
import getShouldSkipClientSideRequest from '../../../src/client/utils/get-should-skip-client-side-request';
import setCacheControlHeader from '../../../pages/utils/set-cache-control-header';
import useGetOffersContent from '../../../pages/utils/use-get-offers-content';
import useTableOfContents from '../../../src/client/cms/utils/use-table-of-contents';
import type { AcquisitionLandingPageContent } from '../../../src/client/pages/acquisition-landing/types';
import type { Offer, Placeholder } from '../../../src/client/components/offers/types';

export type AcquisitionLandingPageTemplateProps = {
  activeOffers: Offer[];
  pageContent: AcquisitionLandingPageContent;
  placeholders: Placeholder[];
  slug: string;
};

const AcquisitionLandingPageTemplate = ({
  activeOffers: serverSideOffers,
  pageContent: serverSidePageContent,
  placeholders: serverSidePlaceholders,
  slug,
}: AcquisitionLandingPageTemplateProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const { activeOffers, placeholders, loading: isOfferContentLoading } = useGetOffersContent({ shouldSkip });

  const { items: pageContent, loading: isPageContentLoading } = useTableOfContents({
    query: GET_TOC_ANNOUNCEMENTS,
    skip: shouldSkip,
    slug,
  });

  const isLoading = isOfferContentLoading || isPageContentLoading;
  const useServerSideContent = shouldSkip || isLoading;

  const content = useServerSideContent
    ? {
        activeOffers: serverSideOffers,
        pageContent: serverSidePageContent,
        placeholders: serverSidePlaceholders,
      }
    : {
        activeOffers,
        pageContent,
        placeholders,
      };

  return <AcquisitionLandingPage {...content} slug={slug} />;
};

export const getTemplateServerSideProps = ({ slug }: { slug: string }) =>
  nextReduxWrapper.getServerSideProps((store) => async ({ res }) => {
    setCacheControlHeader(res);

    const [pageContent, { activeOffers, placeholders }] = await Promise.all([
      getAnnouncementsQuery(slug),
      getOffersContent(),
      fetchAllProductData(store.getState(), store.dispatch),
      fetchProductsFromParams(store.getState(), store.dispatch, { productIds: getSaleableCombinedProductIds() }),
    ]);

    return {
      props: {
        activeOffers,
        pageContent,
        placeholders,
        slug,
      },
    };
  });

export default AcquisitionLandingPageTemplate;
